import { useState, type KeyboardEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ORDER_FULFILLMENT_LABELS,
  ORDER_PAYMENT_LABELS,
  PRESCRIPTION_REVIEW_LABELS,
  PRESCRIPTION_REVIEW_STATUSES,
  type OrderDetail,
  type OrderFulfillment,
  type OrderPayment,
  type OrderRow,
} from '@joker/contracts'
import { useTRPC } from '../trpc'
import { Panel, Tile, Loading, ErrorNote, ViewTitle, fmtDate } from '../ui'
import { displayPhone } from '../phone'
import { GradeChip } from './Jobs'
import { Prescription } from './Prospects'
import { ScannedPrescriptions } from './ScannedPrescriptions'

/**
 * Purchases: every order, one row each, the way the store lists them — number,
 * when, who, whether it is paid and sent, the total and the items. A payment
 * recorded by hand (a wire, a QuickBooks invoice) is an order here too, with no
 * fulfillment and no items. Pressing a row opens the order.
 */
export function Purchases({
  orderId,
  onOpenOrder,
  onBack,
  onOpenPerson,
}: {
  /** The order open now, from the address (/purchases/:id); none for the list. */
  orderId?: string
  onOpenOrder: (id: string) => void
  onBack?: () => void
  onOpenPerson: (id: string) => void
}) {
  const trpc = useTRPC()
  const q = useQuery(trpc.orders.list.queryOptions())
  /* Opens on the orders that stand — paid and not refunded — which is what
     "orders" means to anyone counting sales. */
  const [show, setShow] = useState<'all' | 'paid' | 'onboarding' | 'refunded' | 'unfulfilled'>('paid')
  const [withTests, setWithTests] = useState(false)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<{ col: OrderCol; dir: 'asc' | 'desc' }>({ col: 'at', dir: 'desc' })

  if (orderId) {
    return <OrderPage id={orderId} onBack={onBack ?? (() => {})} onOpenPerson={onOpenPerson} />
  }
  if (q.error) return <ErrorNote error={q.error} />
  if (!q.data) return <Loading what="orders" />

  const real = q.data.filter((o) => !o.isTest)
  const base = withTests ? q.data : real
  const since = Date.now() - 30 * 86_400_000
  const paid = real.filter((o) => o.payment === 'paid')
  /* Net of VAT, the figure Stats and the Pipeline add up, so the three agree. */
  const usd = paid.reduce((n, o) => n + (o.netUsdCents ?? o.usdCents ?? 0), 0)

  const term = search.trim().toLowerCase()
  const list = base
    .filter((o) =>
      show === 'all'
        ? true
        : show === 'paid' || show === 'onboarding'
          ? o.payment === 'paid'
          : show === 'refunded'
            ? o.payment === 'refunded' || o.payment === 'partially_refunded'
            : o.fulfillment === 'unfulfilled' || o.fulfillment === 'partially_fulfilled',
    )
    .filter(
      (o) =>
        !term ||
        [o.number, o.customerName, o.customerEmail, o.leadCode, o.source, o.storeName].some((v) =>
          (v ?? '').toLowerCase().includes(term),
        ),
    )
    .sort((a, b) => {
      const d = sort.dir === 'asc' ? 1 : -1
      const x = sortValue(a, sort.col)
      const y = sortValue(b, sort.col)
      /* Blanks last either way: an empty cell is no value, not the smallest. */
      if (x === null && y !== null) return 1
      if (y === null && x !== null) return -1
      const c = x === null || y === null ? 0 : typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))
      /* Ties fall back to newest first, whichever way the column points. */
      return c * d || b.at.localeCompare(a.at)
    })

  const th = (col: OrderCol, label: string, right = false) => (
    <th
      className={`${right ? 'r ' : ''}sortable`}
      aria-sort={sort.col === col ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() =>
          setSort((s) => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: ASC_FIRST.has(col) ? 'asc' : 'desc' }))
        }
      >
        {label}
        {sort.col === col && <span aria-hidden="true"> {sort.dir === 'asc' ? '↑' : '↓'}</span>}
      </button>
    </th>
  )

  const count = (pred: (o: OrderRow) => boolean) => base.filter(pred).length

  return (
    <>
      <ViewTitle suit="diamond">Direct purchases</ViewTitle>
      <p className="view-sub">
        Every order somebody placed with us, newest first: Wix orders as the store has them, and
        payments recorded by hand. Purchases through an optometrist are on the Optometrists screen.
        Press an order to see everything about it. Team test orders are hidden unless you ask for
        them.
      </p>

      <div className="tiles">
        <Tile k="Orders" v={paid.length} d="paid, not refunded, not a test" />
        <Tile k="Paid, in USD" v={wholeDollars(usd)} d="net of VAT, each at the rate on its day" />
        <Tile k="Refunded" v={real.filter((o) => o.payment !== 'paid' && o.payment !== 'pending').length} d="fully or in part" />
        <Tile k="Last 30 days" v={paid.filter((o) => Date.parse(o.at) >= since).length} d="paid orders" />
      </div>

      <div className="seg seg-inline" role="tablist" aria-label="Which orders">
        {(
          [
            ['paid', 'Paid', count((o) => o.payment === 'paid')],
            ['onboarding', 'Onboarding', count((o) => o.payment === 'paid')],
            ['refunded', 'Refunded', count((o) => o.payment === 'refunded' || o.payment === 'partially_refunded')],
            ['unfulfilled', 'Unfulfilled', count((o) => o.fulfillment === 'unfulfilled' || o.fulfillment === 'partially_fulfilled')],
            ['all', 'All', count(() => true)],
          ] as const
        ).map(([id, label, n]) => (
          <button key={id} type="button" role="tab" aria-selected={show === id} onClick={() => setShow(id)}>
            {label} · {n}
          </button>
        ))}
      </div>

      <div className="toolbar prospect-search">
        <input
          type="search"
          placeholder="Order number, customer, email, PR id…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search orders"
        />
        <label className="ctl check inline-check">
          <input type="checkbox" checked={withTests} onChange={(e) => setWithTests(e.target.checked)} />
          <span>Show team tests</span>
        </label>
        <a className="btn primary export" href="/api/export/orders.csv" download>
          ↓ Export CSV
        </a>
      </div>

      {show === 'onboarding' && <Onboarding orders={list} onOpenOrder={onOpenOrder} onOpenPerson={onOpenPerson} />}

      {show !== 'onboarding' && (
      <Panel title="Orders" note={`${list.length} of ${base.length}`}>
        {list.length === 0 ? (
          <p className="footnote">{search ? 'No order matches that search.' : 'No orders here.'}</p>
        ) : (
          <div className="scroll-x">
            <table className="data orders">
              <thead>
                <tr>
                  {th('number', 'Order')}
                  {th('at', 'Date created')}
                  {th('customer', 'Customer')}
                  {th('rx', 'Prescription')}
                  {th('eligibility', 'Eligibility')}
                  {th('rxReview', 'Prescription approval')}
                  {th('payment', 'Payment')}
                  {th('fulfillment', 'Fulfillment')}
                  {th('total', 'Total', true)}
                  {th('items', 'Items', true)}
                </tr>
              </thead>
              <tbody>
                {list.map((o) => (
                  <tr
                    key={o.id}
                    tabIndex={0}
                    className={o.payment === 'paid' ? undefined : 'dim'}
                    onClick={() => onOpenOrder(o.id)}
                    onKeyDown={(e) => e.key === 'Enter' && onOpenOrder(o.id)}
                  >
                    <td>
                      <strong>{orderLabel(o)}</strong>
                      {o.isTest && <span className="pill test">Test</span>}
                      {o.storeName && o.storeName !== MAIN_STORE && (
                        <>
                          <br />
                          <span className="muted sub-line">{o.storeName}</span>
                        </>
                      )}
                    </td>
                    <td className="num">
                      {fmtDate(o.at)}
                      <br />
                      <span className="muted sub-line">{fmtTime(o.at)}</span>
                    </td>
                    <td>
                      {o.customerName || <span className="muted">Unnamed</span>}
                      {o.leadCode && (
                        <>
                          <br />
                          <span className="muted sub-line">{o.leadCode}</span>
                        </>
                      )}
                    </td>
                    <td>{o.hasPrescription ? 'Yes' : <span className="muted">No</span>}</td>
                    <td>
                      <GradeChip grade={o.eligibility} override={o.eligibilityIsOverride} />
                    </td>
                    <td>
                      <ReviewLabel r={o.prescriptionReview} />
                    </td>
                    <td>
                      <PaymentPill p={o.payment} />
                    </td>
                    <td>
                      <DeliveryPill o={o} />
                      {o.tracking?.number && (
                        <>
                          <br />
                          <TrackingLink carrier={o.tracking.carrier} number={o.tracking.number} link={o.tracking.link} short />
                        </>
                      )}
                    </td>
                    <td className="r num">{money(o.totalCents, o.currency)}</td>
                    <td className="r num">{o.itemCount || <span className="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      )}
    </>
  )
}

/* ------------------------------ onboarding ------------------------------ */

/** Cortex's Demo status: an account made at purchase, before the customer's details are in. */
export function isDemo(status: string | null | undefined): boolean {
  return (status ?? '').trim().toLowerCase() === 'demo'
}

type Step = 'rx' | 'kit' | 'account' | 'real' | 'training'

const STEP_LABELS: Record<Step, string> = {
  rx: 'Prescription',
  kit: 'Kit sent',
  account: 'Oryx account',
  real: 'Details in',
  training: 'Started training',
}

/** Where each paid order stands on the four things that have to happen after a sale. */
function stepsOf(o: OrderRow): Record<Step, boolean> {
  return {
    rx: o.hasPrescription,
    /* A label is the most Joker can see until carrier tracking is connected. */
    kit: o.tracking != null || o.fulfillment === 'fulfilled',
    account: o.onboarding.hasAccount || o.onboarding.sessions > 0,
    /* Made at purchase as Demo; real once their details are in. */
    real: (o.onboarding.hasAccount || o.onboarding.sessions > 0) && !isDemo(o.onboarding.accountStatus),
    training: o.onboarding.sessions > 0,
  }
}

function daysSince(iso: string): number {
  return Math.floor((Date.now() - Date.parse(iso)) / 86_400_000)
}

/**
 * Every paid sale and how far it has got: prescription in, kit sent, Oryx
 * account made, training started. The tiles are the funnel; pressing one lists
 * the sales still missing that step, oldest first, since the longest wait is
 * the one to chase.
 */
function Onboarding({
  orders,
  onOpenOrder,
  onOpenPerson,
}: {
  orders: OrderRow[]
  onOpenOrder: (id: string) => void
  onOpenPerson: (id: string) => void
}) {
  const [missing, setMissing] = useState<Step | null>(null)
  const steps = new Map(orders.map((o) => [o.id, stepsOf(o)]))
  const done = (s: Step) => orders.filter((o) => steps.get(o.id)![s]).length
  const pct = (n: number) => (orders.length === 0 ? 0 : Math.round((n / orders.length) * 100))
  const list = missing
    ? orders.filter((o) => !steps.get(o.id)![missing]).sort((a, b) => a.at.localeCompare(b.at))
    : orders

  const check = (ok: boolean, text: ReactNode, sub?: ReactNode) => (
    <>
      <span className={ok ? 'step-ok' : 'step-wait'}>{ok ? '✓' : '○'}</span> {text}
      {sub && (
        <>
          <br />
          <span className="muted sub-line">{sub}</span>
        </>
      )}
    </>
  )

  return (
    <>
      <div className="tiles onboarding-tiles">
        <Tile k="Paid" v={orders.length} d="sales on this list" />
        {(['rx', 'kit', 'account', 'real', 'training'] as const).map((s) => (
          <button
            key={s}
            type="button"
            className={`tile-button${missing === s ? ' is-on' : ''}`}
            aria-pressed={missing === s}
            onClick={() => setMissing((m) => (m === s ? null : s))}
            title={`List the ${orders.length - done(s)} still without this`}
          >
            <Tile k={STEP_LABELS[s]} v={`${done(s)}`} d={`${pct(done(s))}% · ${orders.length - done(s)} waiting${missing === s ? ' — shown' : ''}`} />
          </button>
        ))}
      </div>

      <Panel
        title={missing ? `Waiting for: ${STEP_LABELS[missing].toLowerCase()}` : 'After the sale'}
        note={missing ? `${list.length} sales, longest wait first` : `${list.length} sales`}
      >
        {missing && (
          <p className="footnote">
            <button type="button" className="linklike" onClick={() => setMissing(null)}>
              ✕ Show every sale
            </button>
          </p>
        )}
        {list.length === 0 ? (
          <p className="footnote">Nobody is waiting on this.</p>
        ) : (
          <div className="scroll-x">
            <table className="data orders">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th className="r">Days since</th>
                  <th>Prescription</th>
                  <th>Kit</th>
                  <th>Oryx account</th>
                  <th>Training</th>
                </tr>
              </thead>
              <tbody>
                {list.map((o) => {
                  const st = steps.get(o.id)!
                  const ob = o.onboarding
                  return (
                    <tr
                      key={o.id}
                      tabIndex={0}
                      onClick={() => onOpenOrder(o.id)}
                      onKeyDown={(e) => e.key === 'Enter' && onOpenOrder(o.id)}
                    >
                      <td>
                        <strong>{orderLabel(o)}</strong>
                        <br />
                        <span className="muted sub-line">{fmtDate(o.at)}</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="linklike"
                          onClick={(e) => {
                            e.stopPropagation()
                            onOpenPerson(ob.traineeId)
                          }}
                        >
                          {o.customerName || 'Unnamed'}
                        </button>
                        {ob.traineeName && (
                          <>
                            <br />
                            <span className="muted sub-line">for {ob.traineeName}</span>
                          </>
                        )}
                      </td>
                      <td className="r num">{daysSince(o.at)}</td>
                      <td>
                        {check(
                          st.rx,
                          st.rx ? 'Received' : 'Not yet',
                          st.rx ? PRESCRIPTION_REVIEW_LABELS[o.prescriptionReview] : null,
                        )}
                      </td>
                      <td>
                        {check(
                          st.kit,
                          o.tracking ? (SHIP_STATUS[o.tracking.status] ?? o.tracking.status) : st.kit ? 'Fulfilled' : 'Not sent',
                          o.tracking?.number ? (
                            <TrackingLink carrier={o.tracking.carrier} number={o.tracking.number} link={o.tracking.link} short />
                          ) : null,
                        )}
                      </td>
                      <td>
                        {check(
                          st.account,
                          !st.account ? 'None yet' : isDemo(ob.accountStatus) ? <span className="pill demo">Demo</span> : 'Created',
                          st.account ? (isDemo(ob.accountStatus) ? 'details not filled in yet' : ob.accountStatus) : null,
                        )}
                      </td>
                      <td>
                        {check(
                          st.training,
                          st.training ? `${ob.sessions} session${ob.sessions === 1 ? '' : 's'}` : 'Not started',
                          st.training && ob.firstSessionAt
                            ? `from ${fmtDate(ob.firstSessionAt)}${ob.lastSessionAt ? ` · last ${fmtDate(ob.lastSessionAt)}` : ''}`
                            : null,
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="footnote">
          <strong>Prescription</strong> is a file uploaded here or one read from the support inbox.{' '}
          <strong>Kit</strong> is the Wix fulfillment: a label means the kit was packed, not that it
          arrived — delivery shows once carrier tracking is connected. <strong>Oryx account</strong>{' '}
          and <strong>training</strong> come from the training app, matched by email: an account made
          under a different address shows as missing until it is connected on the person&rsquo;s page.
        </p>
      </Panel>
    </>
  )
}

type OrderCol = 'number' | 'at' | 'customer' | 'rx' | 'eligibility' | 'rxReview' | 'payment' | 'fulfillment' | 'total' | 'items'

/* Columns whose first press reads best A-Z or "best first"; the rest open largest or newest first. */
const ASC_FIRST = new Set<OrderCol>(['customer', 'eligibility', 'rxReview', 'payment', 'fulfillment'])

const ELIGIBILITY_RANK: Record<string, number> = { suitable: 1, needs_answers: 2, refer: 3, incomplete: 4, not_suitable: 5 }
const PAYMENT_RANK: Record<OrderPayment, number> = { paid: 1, pending: 2, partially_refunded: 3, refunded: 4 }

/** Where the kit is, as a number in the order the steps happen; null for no fulfillment at all. */
function deliveryRank(o: OrderRow): number | null {
  if (o.fulfillment === 'canceled') return 9
  switch (o.tracking?.status) {
    case 'delivered': return 6
    case 'shipped':
    case 'in_transit':
    case 'out_for_delivery': return 5
    case 'exception': return 7
    case 'returned': return 8
    case 'label_created': return 3
  }
  if (o.fulfillment === 'fulfilled') return 4
  if (o.fulfillment === 'partially_fulfilled') return 2
  if (o.fulfillment === 'unfulfilled') return 1
  return null
}

function sortValue(o: OrderRow, col: OrderCol): string | number | null {
  switch (col) {
    case 'number': return o.number ? Number(o.number) || o.number : null
    case 'at': return o.at
    case 'customer': return (o.customerName ?? '').toLowerCase() || null
    case 'rx': return o.hasPrescription ? 1 : 0
    case 'eligibility': return o.eligibility ? (ELIGIBILITY_RANK[o.eligibility] ?? 9) : null
    case 'rxReview': return PRESCRIPTION_REVIEW_STATUSES.indexOf(o.prescriptionReview)
    case 'payment': return PAYMENT_RANK[o.payment] ?? 9
    case 'fulfillment': return deliveryRank(o)
    case 'total': return o.usdCents ?? o.totalCents
    case 'items': return o.itemCount
  }
}

/** The main storefront goes unnamed in the list; any other one is named under the number. */
const MAIN_STORE = 'Oryx — Better Vision Up Close'

const SOURCE_LABELS: Record<string, string> = {
  wire: 'Wire transfer',
  quickbooks: 'QuickBooks',
  optometrist: 'Via optometrist',
  shopify: 'Shopify',
}

function orderLabel(o: Pick<OrderRow, 'number' | 'source'>): string {
  return o.number ? `#${o.number}` : (SOURCE_LABELS[o.source] ?? o.source)
}

const CARRIERS: Record<string, string> = { ups: 'UPS', usps: 'USPS', fedex: 'FedEx', dhl: 'DHL' }
const carrierName = (c: string | null) => (c ? (CARRIERS[c] ?? c) : null)

/* The shipment's current state. Wix can say it was shipped; a carrier
   source moves it on. */
const SHIP_STATUS: Record<string, string> = {
  fulfilled: 'Fulfilled in Wix',
  shipped: 'Shipped',
  label_created: 'Label created',
  in_transit: 'In transit',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  exception: 'Problem',
  returned: 'Returned',
}

/* Each change in its history. */
const SHIP_EVENT: Record<string, string> = {
  fulfilled: 'Marked fulfilled in Wix',
  tracking_added: 'Tracking number added',
  tracking_changed: 'Tracking number changed',
  confirmation_sent: 'Shipping confirmation emailed to the customer',
  delivered: 'Delivered',
  label_created: 'Label created',
  in_transit: 'In transit',
  out_for_delivery: 'Out for delivery',
  exception: 'Delivery problem',
  returned: 'Returned to sender',
}

/** A tracking number that opens the carrier's own tracking page. */
function TrackingLink({
  carrier,
  number,
  link,
  short = false,
}: {
  carrier: string | null
  number: string
  link: string | null
  short?: boolean
}) {
  const href =
    link ??
    (carrier === 'ups'
      ? `https://www.ups.com/track?tracknum=${encodeURIComponent(number)}`
      : carrier === 'usps'
        ? `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(number)}`
        : null)
  const text = short ? `${carrierName(carrier) ?? 'Track'} · …${number.slice(-6)}` : number
  return href ? (
    <a className="tracking" href={href} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()} title={`${carrierName(carrier) ?? ''} ${number}`}>
      {text} ↗
    </a>
  ) : (
    <span className="tracking">{text}</span>
  )
}

function ReviewLabel({ r }: { r: OrderRow['prescriptionReview'] }) {
  return r === 'not_reviewed' ? (
    <span className="muted">{PRESCRIPTION_REVIEW_LABELS.not_reviewed}</span>
  ) : (
    <span className={`rx-review-text is-${r}`}>{PRESCRIPTION_REVIEW_LABELS[r]}</span>
  )
}

export function PaymentPill({ p }: { p: OrderPayment }) {
  return <span className={`pill pay-${p}`}>{ORDER_PAYMENT_LABELS[p]}</span>
}

/**
 * Where the kit is, in four steps: Unfulfilled → Label created → Shipped →
 * Delivered. The carrier's status leads once there is tracking; Wix's own
 * fulfillment state stands in until then. Shipped and Delivered need carrier
 * tracking; until it is connected the furthest an order shows is Label created.
 */
export function DeliveryPill({ o }: { o: Pick<OrderRow, 'fulfillment' | 'tracking'> }) {
  if (o.fulfillment === 'canceled') return <FulfillmentPill f="canceled" />
  const t = o.tracking?.status
  if (t === 'delivered') return <span className="pill ship-delivered">Delivered</span>
  if (t === 'shipped' || t === 'in_transit' || t === 'out_for_delivery')
    return <span className="pill ship-in_transit">Shipped</span>
  if (t === 'exception' || t === 'returned') return <span className={`pill ship-${t}`}>{SHIP_STATUS[t]}</span>
  if (t === 'label_created') return <span className="pill ship-label_created">Label created</span>
  /* Marked fulfilled in Wix with no tracking number: sent some other way. */
  if (o.fulfillment === 'fulfilled' || t === 'fulfilled') return <span className="pill ship-fulfilled">Fulfilled, no tracking</span>
  if (o.fulfillment) return <FulfillmentPill f={o.fulfillment} />
  return <span className="muted">—</span>
}

export function FulfillmentPill({ f }: { f: OrderFulfillment }) {
  return <span className={`pill ful-${f}`}>{ORDER_FULFILLMENT_LABELS[f]}</span>
}

/** A total for a tile: whole dollars, since the cents say nothing at that size. */
function wholeDollars(cents: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0 }).format(
    Math.round(cents / 100),
  )
}

export function money(cents: number | null, currency: string | null): string {
  if (cents == null) return '—'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  }).format(cents / 100)
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

const ACTIVITY_LABELS: Record<string, string> = {
  ORDER_PLACED: 'Order placed',
  ORDER_PAID: 'Paid',
  ORDER_CANCELED: 'Canceled',
  ORDER_FULFILLED: 'Fulfilled',
  ORDER_PARTIALLY_FULFILLED: 'Partly fulfilled',
  ORDER_NOT_FULFILLED: 'Marked not fulfilled',
  REFUND_INITIATED: 'Refund started',
  PAYMENT_REFUNDED: 'Refunded',
  PAYMENT_REFUND_FAILED: 'Refund failed',
  MERCHANT_COMMENT: 'Comment added in Wix',
  TRACKING_NUMBER_ADDED: 'Tracking number added',
  TRACKING_NUMBER_EDITED: 'Tracking number changed',
  SHIPPING_ADDRESS_EDITED: 'Shipping address changed',
  EMAIL_EDITED: 'Email changed',
  PICKUP_READY: 'Ready for pickup',
}

function activityLabel(type: string): string {
  return ACTIVITY_LABELS[type] ?? type.toLowerCase().replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

/* -------------------------------- one order -------------------------------- */

/**
 * Everything about one order: what was bought and for how much, where it went,
 * what happened to it and when, and what Joker counts from it. The customer
 * opens on their lead page when they came through the waitlist or the form,
 * which is where their prescription lives, and on their patient record otherwise.
 */
function OrderPage({
  id,
  onBack,
  onOpenPerson,
}: {
  id: string
  onBack: () => void
  onOpenPerson: (id: string) => void
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const q = useQuery(trpc.orders.byId.queryOptions({ id }))
  const setTest = useMutation(trpc.orders.setTest.mutationOptions({ onSuccess: () => qc.invalidateQueries() }))
  /* The customer's record, for the prescription panel: uploading, approving
     and commenting on it from the order, without opening their page. */
  const customer = useQuery({
    ...trpc.prospects.byId.queryOptions({ id: q.data?.customerId ?? '00000000-0000-0000-0000-000000000000' }),
    enabled: Boolean(q.data?.customerId),
  })

  if (q.error) return <ErrorNote error={q.error} />
  if (!q.data) return <Loading what="the order" />
  const o: OrderDetail = q.data

  const s = o.priceSummary
  const timeline = [...o.activities].filter((a) => a.at).sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''))

  return (
    <>
      <button className="back" onClick={onBack}>
        ← Back
      </button>
      <h2 className="view-title order-title">
        Order {orderLabel(o)} <PaymentPill p={o.payment} />
        {(o.fulfillment || o.tracking) && <DeliveryPill o={o} />}
        {o.isTest && <span className="pill test">Team test</span>}
      </h2>
      <p className="view-sub">
        {fmtDate(o.at)}, {fmtTime(o.at)}
        {o.storeName && <> · {o.storeName}</>}
        {!o.number && <> · recorded by hand</>}
      </p>

      {/* A team test leaves every count, revenue figure and paid date; marking
          one by hand beats the automatic rule either way. */}
      <div className="order-test">
        {o.isTest ? (
          <>
            <span className="muted">
              {o.testReason === 'marked'
                ? 'Marked a team test by hand — not counted anywhere.'
                : o.testReason === 'small'
                  ? 'Counted as a team test automatically: $1 or less (or a small promo-code order).'
                  : 'Counted as a team test because the customer is a test account.'}
            </span>
            <button
              type="button"
              className="btn sm"
              disabled={setTest.isPending}
              onClick={() => setTest.mutate({ id: o.id, mark: false })}
            >
              Count this order
            </button>
          </>
        ) : (
          <>
            <span className="muted">
              {o.testMarked === false ? 'Marked a real sale by hand.' : 'Counted as a sale.'}
            </span>
            <button
              type="button"
              className="btn sm"
              disabled={setTest.isPending}
              onClick={() => setTest.mutate({ id: o.id, mark: true })}
            >
              Mark as test order
            </button>
          </>
        )}
        {o.testMarked != null && (
          <button
            type="button"
            className="linklike"
            disabled={setTest.isPending}
            onClick={() => setTest.mutate({ id: o.id, mark: null })}
          >
            Let the automatic rule decide
          </button>
        )}
        {setTest.error && <span className="bad-text">{setTest.error.message}</span>}
      </div>

      <div className="order-grid">
        <Panel title="Items" note={o.itemCount ? `${o.itemCount} item${o.itemCount === 1 ? '' : 's'}` : 'none listed'}>
          {o.lineItems.length > 0 && (
            <table className="data order-items">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="r">Qty</th>
                  <th className="r">Price</th>
                  <th className="r">Total</th>
                </tr>
              </thead>
              <tbody>
                {o.lineItems.map((li, i) => (
                  <tr key={i}>
                    <td>{li.name}</td>
                    <td className="r num">{li.quantity}</td>
                    <td className="r num">{money(li.priceCents, o.currency)}</td>
                    <td className="r num">{money(li.totalCents, o.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <dl className="summary order-sum">
            {s ? (
              <>
                <dt>Subtotal</dt>
                <dd className="num">{money(s.subtotalCents, o.currency)}</dd>
                <dt>Shipping{o.shippingTitle && <span className="muted"> · {o.shippingTitle}</span>}</dt>
                <dd className="num">{money(s.shippingCents, o.currency)}</dd>
                {s.discountCents > 0 && (
                  <>
                    <dt>Discount{o.promoCode && <span className="muted"> · {o.promoCode}</span>}</dt>
                    <dd className="num">−{money(s.discountCents, o.currency)}</dd>
                  </>
                )}
                <dt>Tax</dt>
                <dd className="num">{money(s.taxCents, o.currency)}</dd>
                <dt className="total">Total</dt>
                <dd className="num total">{money(s.totalCents, o.currency)}</dd>
                {s.refundedCents > 0 && (
                  <>
                    <dt>Refunded{o.refundedAt && <span className="muted"> · {fmtDate(o.refundedAt)}</span>}</dt>
                    <dd className="num bad-text">−{money(s.refundedCents, o.currency)}</dd>
                  </>
                )}
              </>
            ) : (
              <>
                <dt className="total">Total</dt>
                <dd className="num total">{money(o.totalCents, o.currency)}</dd>
              </>
            )}
          </dl>
        </Panel>

        <Panel title="Customer">
          <dl className="summary">
            <dt>Name</dt>
            <dd>{o.customerName || <span className="muted">Unnamed</span>}</dd>
            {o.leadCode && (
              <>
                <dt>Lead</dt>
                <dd>{o.leadCode}</dd>
              </>
            )}
            <dt>Email</dt>
            <dd>{o.customerEmail ?? <span className="muted">not set</span>}</dd>
            <dt>Phone</dt>
            <dd>{displayPhone(o.customerPhone) || <span className="muted">not set</span>}</dd>
            <dt>Ships to</dt>
            <dd>{o.mailingAddress ?? <span className="muted">no address on the order</span>}</dd>
            <dt>Prescription</dt>
            <dd>{o.hasPrescription ? 'On file' : <span className="muted">none yet</span>}</dd>
            <dt>Eligibility</dt>
            <dd>
              <GradeChip grade={o.eligibility} override={o.eligibilityIsOverride} />
            </dd>
            <dt>Approval</dt>
            <dd>
              <ReviewLabel r={o.prescriptionReview} />
            </dd>
          </dl>
          <p className="footnote">
            Their prescription is below, and the same on their own page: a change in either place is
            the same change.
          </p>
          <div className="pform-actions rx-actions">
            <button type="button" className="btn primary" onClick={() => onOpenPerson(o.customerId)}>
              Open {o.customerName ?? 'customer'}
            </button>
          </div>
        </Panel>
      </div>

      {o.shipments.map((sh) => (
        <Panel
          key={sh.id}
          title="Shipment"
          note={sh.fulfilledAt ? `fulfilled ${fmtDate(sh.fulfilledAt)}, ${fmtTime(sh.fulfilledAt)}` : undefined}
        >
          <div className="facts">
            <div className="fact">
              <div className="k">Carrier</div>
              <div className="v">{carrierName(sh.carrier) ?? <span className="muted">not given</span>}</div>
            </div>
            <div className="fact">
              <div className="k">Tracking number</div>
              <div className="v">
                {sh.trackingNumber ? (
                  <TrackingLink carrier={sh.carrier} number={sh.trackingNumber} link={sh.trackingLink} />
                ) : (
                  <span className="muted">not added yet</span>
                )}
              </div>
            </div>
            <div className="fact">
              <div className="k">Status</div>
              <div className="v">
                <span className={`pill ship-${sh.status}`}>{SHIP_STATUS[sh.status] ?? sh.status}</span>
                {sh.statusAt && <span className="muted"> · {fmtDate(sh.statusAt)}</span>}
                {/* What the store can know ends at the label; only the carrier's
                    scan says the package left. */}
                {sh.status === 'label_created' && (
                  <div className="muted sub-line">not yet scanned by {carrierName(sh.carrier) ?? 'the carrier'} — open the tracking link for its latest status</div>
                )}
                {sh.status === 'fulfilled' && <div className="muted sub-line">no tracking number yet</div>}
              </div>
            </div>
          </div>
          {sh.events.length > 0 && (
            <ol className="order-timeline ship-timeline">
              {sh.events.map((e, i) => (
                <li key={i}>
                  <span className="when">
                    {fmtDate(e.at)}, {fmtTime(e.at)}
                  </span>
                  <span>
                    {SHIP_EVENT[e.status] ?? e.status}
                    {e.detail && <span className="muted"> · {e.detail}</span>}
                    <span className="muted"> · {e.source === 'wix' ? 'Wix' : e.source.toUpperCase()}</span>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </Panel>
      ))}

      {timeline.length > 0 && (
        <Panel title="History" note="as Wix logged it">
          <ol className="order-timeline">
            {timeline.map((a, i) => (
              <li key={i}>
                <span className="when">
                  {fmtDate(a.at)}, {fmtTime(a.at!)}
                </span>
                <span>{activityLabel(a.type)}</span>
              </li>
            ))}
          </ol>
        </Panel>
      )}

      {/* The customer's prescription, editable here: the file, its approval,
          comments and history, then every prescription read for them. */}
      {customer.error ? (
        <>
          <ErrorNote error={customer.error} />
          <ScannedPrescriptions personId={o.customerId} />
        </>
      ) : (
        customer.data && <Prescription prospect={customer.data} />
      )}

      <Panel title="What Joker counts">
        <dl className="summary">
          <dt>In USD</dt>
          <dd className="num">
            {o.usdCents == null ? <span className="muted">no rate for {o.currency}</span> : money(o.usdCents, 'USD')}
            {o.fxRate != null && o.currency !== 'USD' && (
              <span className="muted"> · at {o.fxRate.toFixed(4)} on {o.fxRateOn}</span>
            )}
          </dd>
          <dt>Counted as revenue</dt>
          <dd className="num">
            {o.netUsdCents != null ? (
              <>
                {money(o.netUsdCents, 'USD')}
                {o.vatRate ? <span className="muted"> · net of {Math.round(o.vatRate * 100)}% VAT</span> : null}
              </>
            ) : (
              <span className="muted">
                nothing —{' '}
                {o.isTest ? 'a team test' : o.payment === 'pending' ? 'not paid yet' : 'refunded'}
              </span>
            )}
          </dd>
          {(o.storePaymentStatus || o.storeOrderStatus || o.storeFulfillmentStatus) && (
            <>
              <dt>Wix says</dt>
              <dd className="muted">
                {[o.storePaymentStatus, o.storeOrderStatus, o.storeFulfillmentStatus].filter(Boolean).join(' · ')}
              </dd>
            </>
          )}
        </dl>
      </Panel>
    </>
  )
}

/**
 * A person's orders, newest first, on their own page: the number, when, the
 * payment and fulfillment state, the total and what was bought.
 */
export function PersonOrders({ personId, onOpenOrder }: { personId: string; onOpenOrder?: (id: string) => void }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.orders.forPerson.queryOptions({ personId }))
  if (!q.data || q.data.length === 0) return null
  return (
    <Panel title="Orders" note={`${q.data.length}`}>
      <table className="data">
        <thead>
          <tr>
            <th>Order</th>
            <th>Date</th>
            <th>Payment</th>
            <th>Fulfillment</th>
            <th className="r">Total</th>
          </tr>
        </thead>
        <tbody>
          {q.data.map((o) => (
            <tr
              key={o.id}
              {...(onOpenOrder
                ? {
                    tabIndex: 0,
                    onClick: () => onOpenOrder(o.id),
                    onKeyDown: (e: KeyboardEvent) => e.key === 'Enter' && onOpenOrder(o.id),
                  }
                : {})}
            >
              <td>
                <strong>{orderLabel(o)}</strong>
                {o.isTest && <span className="pill test">Test</span>}
              </td>
              <td className="num">
                {fmtDate(o.at)}, {fmtTime(o.at)}
              </td>
              <td>
                <PaymentPill p={o.payment} />
              </td>
              <td><DeliveryPill o={o} /></td>
              <td className="r num">{money(o.totalCents, o.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  )
}
